import { CommonModule } from "@angular/common";
import { Component, Injector, signal } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { ButtonModule } from "primeng/button";
import { InputTextModule } from "primeng/inputtext";
import { TableModule } from "primeng/table";
import { ProfileService } from "src/services/profile/profile.service";
import { MatDialog } from "@angular/material/dialog";
import { first } from "rxjs";
import { ProgressBarModule } from "primeng/progressbar";
import { MenuModule } from "primeng/menu";
import { CBox_GetLockerBaseData } from "@server/services/cbox/public/api/v1/resources/locker/types";
import { CalendarModule } from "primeng/calendar";
import { MultiSelectModule } from "primeng/multiselect";
import {
  CBox_PublicGetAgentActivityDataListItem,
  CBox_PublicGetAgentActivityDataListParams,
  CBox_PublicGetShipmentActivityDataListItem,
  CBox_PublicGetShipmentActivityDataListParams,
  CBox_PublicShipmentActivityDetails,
  CBox_PublicShipmentActivityUserDropOffAction,
  CBox_PublicShipmentActivityUserPickUpAction,
} from "@server/services/cbox/public/api/v1/resources/reports/types";
import { lockerCleanAddress } from "src/helpers/functions";
import { CBoxProfilePickupRequestInfoComponent } from "app/profile/requests/pickup/dialogs/info/cbox-profile-pickup-request-info.component";
import { TableWithFilters } from "app/shared/table-with-filters";
import { CBoxProfileDropOffRequestInfoComponent } from "app/profile/requests/drop-off/dialogs/info/cbox-profile-dropoff-request-info.component";
import { CBoxProfileShipmentActivityReportGenerateDialogComponent } from "../dialogs/generate/shipment-activity/cbox-profile-shipment-activity-report-generate-dialog.component";
import { PaginatedTableWithFilters } from "app/shared/paginated-table-with-filters";

interface DailyTotals {
  [actionType: string]: number;
}

@Component({
  selector: "app-cbox-profile-shipments-reports-list",
  templateUrl: "./cbox-profile-shipments-reports-list.component.html",
  styleUrls: ["./cbox-profile-shipments-reports-list.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    ProgressBarModule,
    MenuModule,
    CalendarModule,
    MultiSelectModule
  ]
})

export class CBoxProfileShipmentsReportsListComponent extends PaginatedTableWithFilters<CBox_PublicGetShipmentActivityDataListItem, CBox_PublicGetShipmentActivityDataListParams> {

  filtersForm: FormGroup;
  dataFetchUrl = "reports/shipments/activity";
  sessionFiltersKey = "profile/reports/shipments/filters";
  sessionFiltersToSave = ["page", "lockers", "interval"];
  localStorageFiltersKey = "profile/reports/shipments/filters";
  localStorageFiltersToSave = ["pageSize"];
  defaultFilters = signal({
    page: 1,
    pageSize: 25,
    interval: [this.oneMonthAgo, this.today]
  });
  expandedLockers: Set<string> = new Set();
  totalsByDates = signal<Record<string, DailyTotals>>({});
  scrollPosition = signal(0);

  constructor(
    inj: Injector,
    private profileService: ProfileService,
    private fb: FormBuilder,
    private dialog: MatDialog) {
      super(inj);
      this.filtersForm = this.fb.group({
        page: [1],
        pageSize: [25],
        lockers: [[]],
        interval: [[this.oneMonthAgo, this.today]]
      });
    }

  async init(): Promise<void> {
    this.profileService.setTitle("Rapoarte lockere");
  }

  getActionCount(type: CBox_PublicGetAgentActivityDataListItem["actions"]["counts"][0]["type"], actions: CBox_PublicGetAgentActivityDataListItem["actions"]["counts"]): number {
    if (!actions) {
      return 0;
    }
    return actions.find((a: CBox_PublicGetAgentActivityDataListItem["actions"]["counts"][0]) => a.type === type)?.count ?? 0;
  }

  lockerCleanAddress(addressParts: CBox_GetLockerBaseData["addressParts"]): string {
    if (!addressParts) {
      return "";
    }
    return lockerCleanAddress(addressParts);
  }

   toggleLockerExpansion(report: CBox_PublicGetShipmentActivityDataListItem): void {
    const uniqueString = this.getReportUniqueString(report);
    if (this.expandedLockers.has(uniqueString)) {
      this.expandedLockers.delete(uniqueString);
    } else {
      this.expandedLockers.add(uniqueString);
    }
  }

  isLockerExpanded(report: any): boolean {
    const uniqueString = this.getReportUniqueString(report);
    return this.expandedLockers.has(uniqueString);
  }

  getShipmentAction(actions: CBox_PublicShipmentActivityUserPickUpAction["actions"], type: "COURIER_PUT_ORDERS" | "COURIER_RETRIEVE_EXPIRED" | "USER_PICK_UP") {
    return actions.find((a) => a.type === type);
  }

  formatTimeInLocker(timeInLocker: number): string {
    if (!timeInLocker) {
      return "";
    }
    const totalMinutes = Math.ceil(timeInLocker / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 0) {
      return `${hours} ${hours === 1 ? "oră" : "ore"} și ${minutes} ${
          minutes === 1 ? "minut" : "minute"}`;
    } else {
      return `${minutes} ${minutes === 1 ? "minut" : "minute"}`;
    }
  }

  generateReport(): void {
    const dialog = this.dialog.open(CBoxProfileShipmentActivityReportGenerateDialogComponent, {
      data: {
        interval: this.filtersForm.value.interval
      },
      minWidth: "min(600px, 100%)",
      disableClose: true,
      autoFocus: false
    });

    dialog.afterClosed().pipe(first()).subscribe(() => {
      this.loading.set(true);
      this.fetchData();
    });
  }

  openRequestDetails(shipment: (
    CBox_PublicShipmentActivityDetails &
    CBox_PublicShipmentActivityUserPickUpAction
  ) |
  (
    CBox_PublicShipmentActivityDetails &
    CBox_PublicShipmentActivityUserDropOffAction
  )): void {
    switch (shipment.type) {
      case "USER_DROP_OFF":
        this.openDropOffRequestInfo(shipment);
        break;

      case "USER_PICK_UP":
        this.openPickUpRequestInfo(shipment);
        break;

      default:
        console.warn("Unknown shipment type: ", shipment);
    }
  }

  openPickUpRequestInfo(shipment: CBox_PublicShipmentActivityDetails & CBox_PublicShipmentActivityUserPickUpAction): void {
    this.scrollPosition.set(window.scrollY);
    window.scrollTo(0, 0);

    const dialog = this.dialog.open(CBoxProfilePickupRequestInfoComponent, {
      data: shipment.details.request,
      minWidth: "min(1200px, 100%)",
      autoFocus: false
    });

    dialog.afterClosed().pipe(first()).subscribe(() => {
      window.scrollTo(0, this.scrollPosition());
    });
  }

  openDropOffRequestInfo(shipment: CBox_PublicShipmentActivityDetails & CBox_PublicShipmentActivityUserDropOffAction): void {
    this.scrollPosition.set(window.scrollY);
    window.scrollTo(0, 0);

    const dialog = this.dialog.open(CBoxProfileDropOffRequestInfoComponent, {
      data: shipment.details.request,
      minWidth: "min(1200px, 100%)",
      autoFocus: false
    });

    dialog.afterClosed().pipe(first()).subscribe(() => {
      window.scrollTo(0, this.scrollPosition());
    });
  }

  getSearchStructure() {
    return Object.entries({
      "lockers[]": this.filtersForm.value.lockers,
      page: +this.filtersForm.value.page,
      pageSize: +this.filtersForm.value.pageSize,
      startDate: this.filtersForm.value.interval[0]?.toISOString(),
      endDate: this.filtersForm.value.interval[1]?.toISOString(),
    })
    .filter(([_, value]) => value?.length || !!value)
    .reduce((acc, [key, value]) => {
      acc[key as keyof CBox_PublicGetAgentActivityDataListParams] = value;
      return acc;
    }, {} as any);
  }

  private getReportUniqueString(report: CBox_PublicGetShipmentActivityDataListItem): string {
    if (!report.locker) {
      return "";
    }

    return `${report.date}-${report.locker.identifier}`;
  }
}